import { useState } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/shared/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/shared/ui/command";
import { Command as CommandPrimitive } from "cmdk";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { AllWorkflowsPropsFragment } from "@/shared/gql/graphql";
import { cn } from "@/shared/lib/utils";
import { inputVariants } from "@/shared/ui/input";
import { useTranslation, T } from "@/shared/i18n";

type NullableWorkflowDropdownProps = {
  isNullable: true;
  workflows: AllWorkflowsPropsFragment[];
  selectedWorkflow: null | AllWorkflowsPropsFragment;
  onWorkflowSelect: (workflow: AllWorkflowsPropsFragment | null) => void;
};

type NonNullableWorkflowDropdownProps = {
  isNullable: false;
  workflows: AllWorkflowsPropsFragment[];
  selectedWorkflow: AllWorkflowsPropsFragment;
  onWorkflowSelect: (workflow: AllWorkflowsPropsFragment) => void;
};

type Props = (
  NullableWorkflowDropdownProps | NonNullableWorkflowDropdownProps
) & {
  className?: string;
  disabled?: boolean;
};

// Presents itself as a field, so it renders from the shared field treatment
// rather than restating a border style of its own. pr-8 leaves room for the
// chevron.
const triggerInputClassName = cn(inputVariants({ inputSize: "sm" }), "pr-8");

export const WorkflowDropdown = ({
  workflows,
  selectedWorkflow,
  onWorkflowSelect,
  isNullable,
  className,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");

  const inputValue = query || selectedWorkflow?.name || "";

  const normalizedQuery = query.trim().toLowerCase();
  const filteredWorkflows = normalizedQuery
    ? workflows.filter((w) => w.name.toLowerCase().includes(normalizedQuery))
    : workflows;

  const handleSelect = (workflow: AllWorkflowsPropsFragment | null) => {
    if (workflow === null) {
      (onWorkflowSelect as NullableWorkflowDropdownProps["onWorkflowSelect"])(
        null,
      );
    } else {
      onWorkflowSelect(workflow);
    }
    setQuery("");
    setIsOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (disabled) return;
    setIsOpen(next);
    if (!next && selectedWorkflow) setQuery("");
  };

  return (
    <Command shouldFilter={false} className={cn("w-full", className)}>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div className="relative w-full">
            <CommandPrimitive.Input
              className={triggerInputClassName}
              value={inputValue}
              onValueChange={(v) => {
                setQuery(v);
                setIsOpen(true);
              }}
              onFocus={() => !disabled && setIsOpen(true)}
              onClick={() => !disabled && setIsOpen(true)}
              placeholder={t("All Workflows")}
              disabled={disabled}
              role="combobox"
              aria-expanded={isOpen}
            />
            {isOpen ? (
              <ChevronUpIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="p-0"
          align="start"
          style={{ width: "var(--radix-popper-anchor-width)" }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <CommandList>
            <CommandEmpty>
              <T id="No workflows found." />
            </CommandEmpty>
            <CommandGroup>
              {isNullable && (
                <CommandItem
                  value="all-workflows"
                  onSelect={() => handleSelect(null)}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 h-4 w-4",
                      !selectedWorkflow ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <T id="All Workflows" />
                </CommandItem>
              )}
              {filteredWorkflows.map((workflow) => (
                <CommandItem
                  key={workflow.id}
                  value={workflow.id}
                  onSelect={() => handleSelect(workflow)}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedWorkflow?.id === workflow.id
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {workflow.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
};
